import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  Svg,
  Path,
  Circle,
  G,
  Rect
} from '@react-pdf/renderer';

const CTB_LOGO_URL = 'https://ctbtstbucket.blob.core.windows.net/files/a81ff888-74f3-4bd3-96b5-38cf2fde6b15.png';

export interface DataDeletionCertificateProps {
  certRef?: string;
  issueDate?: string;
  companyName?: string;
  deletionDate?: string;
  engagementName?: string;
  reportRef?: string;
  dataDeletedItems?: string[];
}

const styles = StyleSheet.create({
  page: {
    width: '100%',
    height: '100%',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    flexDirection: 'column',
    position: 'relative'
  },
  header: {
    backgroundColor: '#0B132B',
    paddingTop: 36,
    paddingBottom: 28,
    paddingHorizontal: 40,
    position: 'relative'
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24
  },
  logoText: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: 1,
    lineHeight: 1.25,
    marginLeft: 10,
    textTransform: 'uppercase'
  },
  title: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#FFFFFF',
    letterSpacing: -0.5
  },
  accentBar: {
    height: 4,
    backgroundColor: '#00E676',
    width: '100%'
  },
  bodyWrapper: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden'
  },
  watermarkContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%'
  },
  bodyContent: {
    flex: 1,
    paddingTop: 36,
    paddingBottom: 24,
    paddingHorizontal: 40,
    position: 'relative',
    flexDirection: 'column'
  },
  introParagraph: {
    textAlign: 'center',
    fontSize: 10.5,
    lineHeight: 1.6,
    color: '#475569',
    marginBottom: 28,
    paddingHorizontal: 16
  },
  boldText: {
    fontWeight: 'bold',
    color: '#0F172A'
  },
  metaGrid: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    marginBottom: 24,
    overflow: 'hidden'
  },
  gridRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0'
  },
  gridRowLast: {
    flexDirection: 'row',
    borderBottomWidth: 0
  },
  gridCellLeft: {
    width: '50%',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRightWidth: 1,
    borderRightColor: '#E2E8F0'
  },
  gridCellRight: {
    width: '50%',
    paddingVertical: 12,
    paddingHorizontal: 16
  },
  metaLabel: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4
  },
  metaValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#0F172A'
  },
  dataDeletedBox: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    borderRadius: 8,
    padding: 18,
    marginBottom: 32
  },
  dataDeletedTitle: {
    fontSize: 8.5,
    fontWeight: 'bold',
    color: '#16A34A',
    letterSpacing: 0.5,
    marginBottom: 10,
    textTransform: 'uppercase'
  },
  bulletRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5
  },
  bulletDot: {
    fontSize: 10,
    color: '#16A34A',
    marginRight: 6
  },
  bulletText: {
    fontSize: 9.5,
    color: '#334155'
  },
  signatureStampRow: {
    marginTop: 'auto',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: 8
  },
  signatureBlock: {
    width: 200
  },
  signatureLine: {
    width: '100%',
    height: 1.5,
    backgroundColor: '#1E293B',
    marginBottom: 8
  },
  signatoryTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#0F172A'
  },
  signatorySub: {
    fontSize: 8.5,
    color: '#64748B',
    marginTop: 2
  },
  footer: {
    backgroundColor: '#0B132B',
    borderTopWidth: 2,
    borderTopColor: '#00E676',
    paddingVertical: 14,
    paddingHorizontal: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerLeft: {
    fontSize: 8,
    color: '#CBD5E1'
  },
  footerLeftBold: {
    fontSize: 8,
    color: '#FFFFFF',
    fontWeight: 'bold'
  },
  footerRight: {
    fontSize: 7.5,
    color: '#94A3B8'
  }
});

export const DataDeletionCertificatePDF: React.FC<DataDeletionCertificateProps> = ({
  certRef = 'CTB-DEL-2026-1004',
  issueDate = '11 September 2026',
  companyName = 'BrightPay',
  deletionDate = '30 August 2026',
  engagementName = 'One-time penetration test',
  reportRef = 'CTB-RPT-2025-0788',
  dataDeletedItems = [
    'Pentest report and all associated findings',
    'Related engagement data held on the platform',
    'Backups containing the above'
  ]
}) => {
  return (
    <Document title={`Certificate_of_Data_Deletion_${companyName}`}>
      <Page size="A4" style={styles.page}>
        {/* HEADER */}
        <View style={styles.header}>
          <View style={styles.logoRow}>
            <Image src={CTB_LOGO_URL} style={{ width: 145, height: 38, objectFit: 'contain' }} />
          </View>
          <Text style={styles.title}>Certificate of Data Deletion</Text>
        </View>

        {/* GREEN ACCENT LINE */}
        <View style={styles.accentBar} />

        {/* MAIN BODY */}
        <View style={styles.bodyWrapper}>
          {/* BACKGROUND DIAMOND SHAPE COMPONENT */}
          <View style={styles.watermarkContainer}>
            <Svg width="100%" height="100%" viewBox="0 0 595.28 644">
              <G transform="translate(297.64, 322) rotate(45)">
                <Rect
                  x={-202.5}
                  y={-202.5}
                  width={405}
                  height={405}
                  rx={24}
                  ry={24}
                  fill="#F7F8F8"
                />
              </G>
            </Svg>
          </View>

          <View style={styles.bodyContent}>
            {/* INTRO PARAGRAPH */}
            <Text style={styles.introParagraph}>
              This is to certify that <Text style={styles.boldText}>Capture The Bug</Text> has permanently deleted the data described below in accordance with its data retention policy and the terms of the applicable Statement of Work. The data has been removed from all production systems and backups and is <Text style={styles.boldText}>unrecoverable</Text>.
            </Text>

            {/* METADATA GRID TABLE */}
            <View style={styles.metaGrid}>
              {/* ROW 1 */}
              <View style={styles.gridRow}>
                <View style={styles.gridCellLeft}>
                  <Text style={styles.metaLabel}>CERTIFICATE REFERENCE</Text>
                  <Text style={styles.metaValue}>{certRef}</Text>
                </View>
                <View style={styles.gridCellRight}>
                  <Text style={styles.metaLabel}>DATE OF ISSUE</Text>
                  <Text style={styles.metaValue}>{issueDate}</Text>
                </View>
              </View>

              {/* ROW 2 */}
              <View style={styles.gridRow}>
                <View style={styles.gridCellLeft}>
                  <Text style={styles.metaLabel}>ORGANIZATION</Text>
                  <Text style={styles.metaValue}>{companyName}</Text>
                </View>
                <View style={styles.gridCellRight}>
                  <Text style={styles.metaLabel}>DATE OF DELETION</Text>
                  <Text style={styles.metaValue}>{deletionDate}</Text>
                </View>
              </View>

              {/* ROW 3 */}
              <View style={styles.gridRowLast}>
                <View style={styles.gridCellLeft}>
                  <Text style={styles.metaLabel}>ENGAGEMENT</Text>
                  <Text style={styles.metaValue}>{engagementName}</Text>
                </View>
                <View style={styles.gridCellRight}>
                  <Text style={styles.metaLabel}>REPORT REFERENCE</Text>
                  <Text style={styles.metaValue}>{reportRef}</Text>
                </View>
              </View>
            </View>

            {/* DATA DELETED BOX */}
            <View style={styles.dataDeletedBox}>
              <Text style={styles.dataDeletedTitle}>DATA DELETED</Text>
              {dataDeletedItems.map((item, index) => (
                <View key={index} style={styles.bulletRow}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.bulletText}>{item}</Text>
                </View>
              ))}
            </View>

            {/* SIGNATURE AND STAMP ROW */}
            <View style={styles.signatureStampRow}>
              <View style={styles.signatureBlock}>
                <View style={styles.signatureLine} />
                <Text style={styles.signatoryTitle}>Authorised Signatory</Text>
                <Text style={styles.signatorySub}>Security Operations, Capture The Bug</Text>
              </View>

              {/* CIRCULAR STAMP BADGE */}
              <Svg width="100" height="100" viewBox="0 0 104 104">
                <Circle cx="52" cy="52" r="46" stroke="#16A34A" strokeWidth="2.5" fill="none" />
                <Circle cx="52" cy="52" r="40" stroke="#16A34A" strokeWidth="1" fill="none" />
                <G transform="translate(52, 52) rotate(-12)">
                  <Text
                    x="0"
                    y="-5"
                    style={{
                      fontSize: 10.5,
                      fontWeight: 'bold',
                      fill: '#16A34A',
                      letterSpacing: 1.5,
                      textAnchor: 'middle'
                    }}
                  >
                    VERIFIED
                  </Text>
                  <Text
                    x="0"
                    y="9"
                    style={{
                      fontSize: 8.5,
                      fontWeight: 'bold',
                      fill: '#16A34A',
                      letterSpacing: 1.2,
                      textAnchor: 'middle'
                    }}
                  >
                    DELETED
                  </Text>
                </G>
              </Svg>
            </View>
          </View>
        </View>

        {/* FOOTER */}
        <View style={styles.footer}>
          <Text style={styles.footerLeft}>
            <Text style={styles.footerLeftBold}>Capture The Bug</Text>  ·  Secure your business. Stay audit-ready.
          </Text>
          <Text style={styles.footerRight}>
            System-generated - valid without a physical signature
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default DataDeletionCertificatePDF;
